import { Alert, Button, Modal, Spin, Table, Tag, Typography } from "antd";
import PropTypes from "prop-types";
import { get, keyBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { IMPORT_KINDS, useProcessImport } from "./useProcessImport";

const { Text } = Typography;

const buildRows = ({ processInput, embeddedScreens }) => {
  const statusNameById = keyBy(get(processInput, "processStatuses", []), "id");
  return (embeddedScreens || []).map((screen) => ({
    key: `embedded-${screen.statusId}`,
    statusName: get(statusNameById[screen.statusId], "name") || screen.name,
    screenName: screen.name,
  }));
};

const formatFieldError = (entry) => {
  const params = entry?.params || {};
  const detail = Object.keys(params).length ? ` ${JSON.stringify(params)}` : "";
  return `${entry?.key || "ERROR"}${detail}`;
};

const ImportSummaryModal = ({
  file,
  existingProcess,
  liveStepIds,
  onImportSteps,
  requireEmbeddedScreens,
  onClose,
  onImported,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const IMPORT = t("IMPORT_SUMMARY", { returnObjects: true });
  const IMPORT_FIELDS = t("IMPORT_FIELDS", { returnObjects: true });
  const draftNote = t("CANVAS_EDITOR.DRAFT.IMPORT_NOTE");
  const { prepareImport, runImport } = useProcessImport({
    liveStepIds,
    onImportSteps,
  });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState([]);
  const [fieldsResult, setFieldsResult] = useState(null);

  const resolveErrorMessage = (err) => {
    const code = err?.message;
    if (code && IMPORT.ERRORS && IMPORT.ERRORS[code]) {
      return IMPORT.ERRORS[code];
    }
    if (code && IMPORT_FIELDS.ERRORS && IMPORT_FIELDS.ERRORS[code]) {
      return IMPORT_FIELDS.ERRORS[code];
    }
    return code || IMPORT.ERROR_TITLE;
  };

  const reviewColumns = useMemo(
    () => [
      {
        title: IMPORT.COLUMNS.STATUS,
        dataIndex: "statusName",
        render: (value, row) => (
          <div>
            <div>{value}</div>
            <Text type="secondary" className="canvas-editor__meta-xs">
              {row.screenName}
            </Text>
          </div>
        ),
      },
      {
        title: IMPORT.COLUMNS.SCREEN,
        dataIndex: "screenName",
        width: 130,
        render: () => <Tag color="purple">{IMPORT.TAGS.EMBEDDED}</Tag>,
      },
    ],
    [IMPORT],
  );

  const fieldColumns = useMemo(
    () => [
      {
        title: IMPORT_FIELDS.COLUMNS.STATUS,
        dataIndex: "isNew",
        width: 90,
        render: (isNew) =>
          isNew ? (
            <Tag color="green">{IMPORT_FIELDS.TAGS.NEW}</Tag>
          ) : (
            <Tag color="blue">{IMPORT_FIELDS.TAGS.EXISTING}</Tag>
          ),
      },
      {
        title: IMPORT_FIELDS.COLUMNS.FIELD,
        dataIndex: "key",
        render: (key) => <Text code>{`custom.${key}`}</Text>,
      },
      {
        title: IMPORT_FIELDS.COLUMNS.LABEL,
        dataIndex: "label",
      },
      {
        title: IMPORT_FIELDS.COLUMNS.TYPE,
        dataIndex: "type",
        width: 120,
      },
    ],
    [IMPORT_FIELDS],
  );

  const resultColumns = useMemo(
    () => [
      {
        title: IMPORT.COLUMNS.STATUS,
        dataIndex: ["status", "name"],
        render: (value, row) => value || get(row, "status.id"),
      },
      {
        title: IMPORT.COLUMNS.RESULT,
        dataIndex: "screen",
        width: 130,
        render: (screen) =>
          screen ? (
            <Tag color="green">{IMPORT.TAGS.IMPORTED}</Tag>
          ) : (
            <Tag color="red">{IMPORT.TAGS.FAILED}</Tag>
          ),
      },
      {
        title: IMPORT.COLUMNS.ERROR,
        dataIndex: "error",
        render: (entryError) =>
          entryError ? (
            <Text type="danger" className="canvas-editor__meta-danger">
              {entryError}
            </Text>
          ) : null,
      },
    ],
    [IMPORT],
  );

  useEffect(() => {
    if (!file) return undefined;
    let cancelled = false;
    setPhase("loading");
    setError(null);
    setResults([]);
    setFieldsResult(null);
    setPrepared(null);
    setRows([]);
    (async () => {
      try {
        const data = await prepareImport(file, { existingProcess });
        if (cancelled) return;
        setPrepared(data);
        setRows(data.kind === IMPORT_KINDS.CANVAS ? buildRows(data) : []);
        setPhase("review");
      } catch (err) {
        if (cancelled) return;
        setError(resolveErrorMessage(err));
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, existingProcess, prepareImport]);

  const fieldsPreview = get(prepared, "fieldsPreview");
  const fieldRows = get(fieldsPreview, "fields", []) || [];
  const fieldMappings = get(fieldsPreview, "mappings", []) || [];
  const fieldErrors = get(fieldsPreview, "errors", []) || [];
  const fieldWarnings = get(fieldsPreview, "warnings", []) || [];
  const fieldsValid = !fieldsPreview || fieldsPreview.valid !== false;
  const isFieldsOnly = prepared?.kind === IMPORT_KINDS.FIELDS;
  const intoDraft = !isFieldsOnly && Boolean(existingProcess);

  const onConfirm = async () => {
    if (!prepared) return;
    if (!fieldsValid) return;
    setPhase("importing");
    try {
      const {
        id,
        results: importResults,
        fieldsResult: nextFieldsResult,
      } = await runImport(prepared);
      setResults(importResults || []);
      setFieldsResult(nextFieldsResult);
      setPhase("done");
      if (onImported) onImported(id, importResults);
    } catch (err) {
      setError(resolveErrorMessage(err));
      setPhase("error");
    }
  };

  const failedCount = results.filter((result) => !result.screen).length;

  const confirmLabel = () => {
    if (isFieldsOnly) {
      return t("IMPORT_FIELDS.IMPORT", {
        newCount: get(fieldsPreview, "newFields", 0),
        total: get(fieldsPreview, "totalFields", 0),
      });
    }
    if (fieldsPreview) {
      return t("IMPORT_SUMMARY.IMPORT_WITH_FIELDS", {
        total: rows.length,
        newCount: get(fieldsPreview, "newFields", 0),
      });
    }
    return t("IMPORT_SUMMARY.IMPORT", { total: rows.length });
  };

  const footer = () => {
    if (phase === "review")
      return [
        { key: "cancel", text: IMPORT.CANCEL, onClick: onClose },
        {
          key: "confirm",
          text: confirmLabel(),
          type: "primary",
          onClick: onConfirm,
          disabled: !fieldsValid,
        },
      ];
    if (phase === "done" || phase === "error")
      return [
        { key: "close", text: IMPORT.CLOSE, type: "primary", onClick: onClose },
      ];
    return null;
  };

  const title = () => {
    if (isFieldsOnly) return IMPORT_FIELDS.TITLE;
    return existingProcess ? IMPORT.TITLE_UPDATE : IMPORT.TITLE_CREATE;
  };

  const renderFieldsSection = () => {
    if (!fieldsPreview) return null;
    return (
      <div className={isFieldsOnly ? undefined : "m-top-24"}>
        {!isFieldsOnly && (
          <div className="m-bottom-16">
            <Text strong>{IMPORT.FIELDS_SECTION_TITLE}</Text>
          </div>
        )}
        <div className="m-bottom-20">
          <Text>
            {t("IMPORT_FIELDS.SUMMARY", {
              total: fieldsPreview.totalFields || 0,
              newCount: fieldsPreview.newFields || 0,
              existing: fieldsPreview.existingFields || 0,
            })}
          </Text>
        </div>
        {!fieldsValid && (
          <Alert
            className="m-bottom-20"
            type="error"
            showIcon
            message={IMPORT_FIELDS.INVALID_TITLE}
            description={
              <ul>
                {fieldErrors.map((entry) => (
                  <li key={formatFieldError(entry)}>
                    {formatFieldError(entry)}
                  </li>
                ))}
              </ul>
            }
          />
        )}
        {fieldWarnings.length > 0 && (
          <Alert
            className="m-bottom-20"
            type="warning"
            showIcon
            message={IMPORT_FIELDS.WARNINGS_TITLE || "Warnings"}
            description={
              <ul>
                {fieldWarnings.map((warning) => (
                  <li key={warning}>
                    {(IMPORT_FIELDS.WARNINGS &&
                      IMPORT_FIELDS.WARNINGS[warning]) ||
                      warning}
                  </li>
                ))}
              </ul>
            }
          />
        )}
        <Table
          size="small"
          pagination={false}
          columns={fieldColumns}
          dataSource={fieldRows.map((field) => ({
            ...field,
            key: field.key,
          }))}
        />
        {fieldMappings.length > 0 && (
          <div className="m-top-24">
            <Text strong>{IMPORT_FIELDS.MAPPINGS_TITLE}</Text>
            <ul>
              {fieldMappings.map((mapping) => (
                <li key={`${mapping.source}-${mapping.canonical}`}>
                  <Text code>{mapping.source}</Text>
                  {" → "}
                  <Text code>{mapping.canonical}</Text>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={!!file}
      title={title()}
      onCancel={onClose}
      width={isFieldsOnly ? 860 : 760}
      footer={(footer() || []).map((button) => (
        <Button
          key={button.key}
          type={button.type === "primary" ? "primary" : "default"}
          onClick={button.onClick}
          disabled={button.disabled}
        >
          {button.text}
        </Button>
      ))}
    >
      {phase === "loading" && (
        <div className="canvas-editor__centered">
          <Spin tip={IMPORT.LOADING} />
        </div>
      )}

      {phase === "error" && (
        <Alert
          type="error"
          showIcon
          message={IMPORT.ERROR_TITLE}
          description={error}
        />
      )}

      {phase === "review" && prepared && (
        <>
          <div className="m-bottom-20">
            <Text strong>{get(prepared, "processInput.name")}</Text>{" "}
            {get(prepared, "processInput.type") && (
              <Text type="secondary">
                ({get(prepared, "processInput.type")})
              </Text>
            )}
          </div>

          {intoDraft && (
            <Alert
              className="m-bottom-20"
              type="info"
              showIcon
              message={draftNote}
            />
          )}

          {!isFieldsOnly &&
            requireEmbeddedScreens &&
            !(prepared.embeddedScreens || []).length && (
              <Alert
                className="m-bottom-20"
                type="warning"
                showIcon
                message={IMPORT.NO_EMBEDDED_TITLE}
                description={IMPORT.NO_EMBEDDED_DESC}
              />
            )}

          {!isFieldsOnly && rows.length > 0 && (
            <Table
              size="small"
              pagination={false}
              columns={reviewColumns}
              dataSource={rows}
            />
          )}

          {renderFieldsSection()}
        </>
      )}

      {phase === "importing" && (
        <div className="canvas-editor__centered">
          <Spin tip={IMPORT.IMPORTING} />
        </div>
      )}

      {phase === "done" && (
        <>
          {intoDraft && (
            <Alert
              className="m-bottom-20"
              type="info"
              showIcon
              message={draftNote}
            />
          )}
          {fieldsResult && (
            <Alert
              className="m-bottom-20"
              type="success"
              showIcon
              message={IMPORT_FIELDS.SUCCESS_TITLE}
              description={t("IMPORT_FIELDS.SUCCESS_DESC", {
                created: fieldsResult.created || 0,
                updated: fieldsResult.updated || 0,
                skipped: fieldsResult.skipped || 0,
              })}
            />
          )}
          {!isFieldsOnly && failedCount > 0 && (
            <Alert
              className="m-bottom-20"
              type="warning"
              showIcon
              message={t("IMPORT_SUMMARY.FAILED_COUNT", { count: failedCount })}
            />
          )}
          {!isFieldsOnly && results.length > 0 && (
            <Table
              size="small"
              pagination={false}
              columns={resultColumns}
              dataSource={results.map((result, index) => ({
                ...result,
                key: get(result, "status.id") || index,
              }))}
            />
          )}
          {isFieldsOnly && !fieldsResult && (
            <Alert
              type="success"
              showIcon
              message={IMPORT_FIELDS.SUCCESS_TITLE}
            />
          )}
        </>
      )}
    </Modal>
  );
};

ImportSummaryModal.propTypes = {
  file: PropTypes.object,
  existingProcess: PropTypes.object,
  liveStepIds: PropTypes.arrayOf(PropTypes.string),
  onImportSteps: PropTypes.func,
  requireEmbeddedScreens: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onImported: PropTypes.func,
};

ImportSummaryModal.defaultProps = {
  file: null,
  existingProcess: null,
  liveStepIds: [],
  onImportSteps: null,
  requireEmbeddedScreens: false,
  onImported: null,
};

export default ImportSummaryModal;
